import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, ChevronLeft, ChevronRight, Briefcase, User, Tag, Check, Calendar, Building2, ArrowUpDown, ArrowUp, ArrowDown, Download, CreditCard, Landmark, Pencil, X, Save, TrendingUp, Link2, Unlink, ChevronDown, ChevronUp as ChevronUpIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_CATEGORIES = ["Travel", "Food & Dining", "Shopping", "Transportation", "Services", "Subscriptions", "Utilities", "Entertainment", "Health", "Education", "Fees & Charges", "Home", "Personal Care", "Insurance", "Fuel", "Other"];
const INCOME_CATEGORIES = ["Salary", "Freelance", "Business Income", "Investment Returns", "Interest", "Rental Income", "Refund", "Cashback", "Gift", "Other Income"];
const fmtINR = (v) => v?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00';
const amtColor = (tx) => {
  if (tx.is_credit) {
    const t = tx.tag || '';
    if (t === 'Income / Salary' || t.startsWith('Business / Reimbursement') || t === 'Business / Reimbursement Return') return 'text-emerald-700';
    return 'text-[#4A7A5D]';
  }
  const t = tx.tag || '';
  if (t.startsWith('Business') || t === 'business') return 'text-red-600';
  if (t.startsWith('Personal') || t === 'personal') return 'text-orange-600';
  return 'text-[#1C1C19]';
};
const CARD_SOURCES = ['amex', 'axis', 'visa', 'mastercard', 'rupay'];
const isCardSource = (source) => {
  if (!source) return false;
  const s = source.toLowerCase();
  return CARD_SOURCES.some(c => s.includes(c)) || s.includes('credit card') || s.includes('card');
};
const EntityIcon = ({ source }) => isCardSource(source)
  ? <CreditCard size={11} className="text-[#5C5C55] shrink-0" />
  : <Landmark size={11} className="text-[#5C5C55] shrink-0" />;


function TagBadge({ tag, tagSource, isCredit, category }) {
  const baseTag = tag?.startsWith('Business') || tag === 'business' ? 'business'
    : tag?.startsWith('Personal') || tag === 'personal' ? 'personal'
    : tag?.startsWith('Income') || tag === 'income' ? 'income'
    : tag === 'junk' ? 'junk' : tag === 'minor' ? 'minor' : 'untagged';

  // CC Payments are credits but NOT income — they are transfers between accounts
  const isCCPayment = category && category.toLowerCase().includes('cc payment');
  // For credits, determine if business income or personal income
  const isBizIncome = isCredit && (tag === 'Income / Salary' || tag?.startsWith('Business / Reimbursement'));
  const displayTag = isCCPayment ? 'cc_payment'
    : isCredit && baseTag !== 'junk' && baseTag !== 'minor' && baseTag !== 'untagged'
    ? (isBizIncome ? 'income_biz' : 'income_personal')
    : baseTag;

  const styles = {
    business: 'text-red-600',
    personal: 'text-orange-600',
    cc_payment: 'text-blue-600',
    income_biz: 'text-emerald-700',
    income_personal: 'text-[#4A7A5D]',
    junk: 'text-[#5C5C55]',
    minor: 'text-[#7C6B4F]',
    untagged: 'text-[#5C5C55]',
  };
  const labels = {
    business: 'Expense - Business',
    personal: 'Expense - Personal',
    cc_payment: 'Payment Made',
    income_biz: 'Income - Business',
    income_personal: 'Income - Personal',
    junk: 'Junk',
    minor: 'Minor',
    untagged: 'Untagged',
  };
  const iconTag = displayTag.startsWith('income') ? 'income' : displayTag;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wide ${styles[displayTag] || styles.untagged}`}>
      {iconTag === 'business' && <Briefcase size={10} />}
      {iconTag === 'personal' && <User size={10} />}
      {iconTag === 'income' && <TrendingUp size={10} />}
      {iconTag === 'untagged' && <Tag size={10} />}
      {labels[displayTag] || tag}

    </span>
  );
}

function SortHeader({ label, field, currentSort, onSort }) {
  const isActive = currentSort.field === field;
  const isAsc = isActive && currentSort.order === 'asc';
  return (
    <button onClick={() => onSort(field)} data-testid={`sort-${field}`}
      className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-medium hover:text-[#1C1C19] transition-colors">
      {label}
      {isActive ? (isAsc ? <ArrowUp size={10} className="text-[#284A33]" /> : <ArrowDown size={10} className="text-[#284A33]" />) : <ArrowUpDown size={10} className="opacity-30" />}
    </button>
  );
}

export default function TransactionsPage() {
  const [searchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const initialFilters = {
    tag: searchParams.get('tag') || 'untagged',
    category: 'all', search: '',
    fy: 'all', month: 'all', junk: 'hide',
    source: 'all', card: 'all', country: 'all'
  };
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState({ field: 'date', order: 'desc' });
  const [filterOptions, setFilterOptions] = useState({ categories: [], tags: [], months: [], sources: [], countries: [], financial_years: { india: [], uae: [] } });
  const [subCatOptions, setSubCatOptions] = useState({ business: [], personal: [] });
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({ tag: '', sub_category: '', category: '' });
  const [txnTab, setTxnTab] = useState(searchParams.get('tag') === 'tagged' ? 'tagged' : 'pending');
  const [flowTab, setFlowTab] = useState('all');

  const switchTab = (tab) => {
    setTxnTab(tab);
    setFlowTab('all');
    if (tab === 'personal') {
      setFilters(f => ({ ...f, tag: 'personal_all', junk: 'hide', category: 'all', source: 'all', card: 'all', country: 'all' }));
    } else if (tab === 'business') {
      setFilters(f => ({ ...f, tag: 'business_all', junk: 'hide', category: 'all', source: 'all', card: 'all', country: 'all' }));
    } else if (tab === 'dubai_recovery') {
      setFilters(f => ({ ...f, tag: 'dubai_recovery', junk: 'hide', category: 'all', source: 'all', card: 'all', country: 'all' }));
    } else {
      setFilters(f => ({ ...f, tag: 'untagged', junk: 'hide', category: 'all', source: 'all', card: 'all', country: 'all' }));
    }
    setPage(1);
    setSelected(new Set());
  };

  const switchFlowTab = (flow) => {
    setFlowTab(flow);
    if (flow === 'junk') {
      setFilters(f => ({ ...f, junk: 'only' }));
    } else if (flow === 'minor') {
      setFilters(f => ({ ...f, junk: 'hide' }));
    } else {
      setFilters(f => ({ ...f, junk: 'hide' }));
    }
    setPage(1);
    setSelected(new Set());
  };

  // Link / Split state
  const [linkDialog, setLinkDialog] = useState({ open: false, note: '' });
  const [linksMap, setLinksMap] = useState({});
  const [expandedLinks, setExpandedLinks] = useState(new Set());

  // Custom dialogs
  const [othersDialog, setOthersDialog] = useState({ open: false, parentTag: '', customName: '' });
  const [catDialog, setCatDialog] = useState({ open: false, customName: '' });

  const fetchFilters = useCallback(async () => {
    try {
      const tagParam = txnTab === 'business' ? 'business_all' : txnTab === 'personal' ? 'personal_all' : txnTab === 'dubai_recovery' ? 'dubai_recovery' : 'untagged';
      const [fRes, scRes, catRes] = await Promise.all([
        axios.get(`${API}/transactions/filters`, { params: { tag: tagParam }, withCredentials: true }),
        axios.get(`${API}/sub-categories`, { withCredentials: true }),
        axios.get(`${API}/categories`, { withCredentials: true }),
      ]);
      setFilterOptions(fRes.data);
      setSubCatOptions(scRes.data);
      setCategories(catRes.data.categories || DEFAULT_CATEGORIES);
    } catch (err) { console.error('Filters error:', err); }
  }, [txnTab]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, sort_by: sort.field, sort_order: sort.order };
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all' && k !== 'junk' && k !== 'search' && k !== 'card') params[k] = v; });
      if (filters.search) params.search = filters.search;
      if (filters.junk === 'only') params.junk = 'only';
      else if (filters.junk === 'include') params.junk = 'include';
      if (filters.card && filters.card !== 'all') {
        // Determine if it's a cardholder name or a card number
        const srcInfo = (filterOptions.sources || []).find(s => s.name === filters.source);
        const holders = srcInfo?.holders?.filter(h => h && h !== 'None' && h !== 'Unknown') || [];
        if (holders.length > 1 && holders.includes(filters.card)) {
          params.cardholder = filters.card;
        } else {
          params.card_last_four = filters.card;
        }
      }
      if (flowTab !== 'all') params.flow = flowTab;
      const { data } = await axios.get(`${API}/transactions`, { params, withCredentials: true });
      setTransactions(data.transactions);
      setLinksMap(data.links || {});
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) { console.error('Transactions error:', err); }
    finally { setLoading(false); }
  }, [page, filters, sort, flowTab]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const activeFyList = filterOptions.financial_years?.india || [];
  const handleSort = (field) => { setSort(prev => ({ field, order: prev.field === field ? (prev.order === 'desc' ? 'asc' : 'desc') : 'asc' })); setPage(1); };
  const handleFilterChange = (key, value) => { setFilters(f => ({ ...f, [key]: value })); setPage(1); setSelected(new Set()); };
  const fmtMonth = (m) => { if (!m) return m; const [y, mo] = m.split('-'); const ms = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${ms[parseInt(mo)]} ${y}`; };
  const toggleSelect = (id) => { setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleAll = () => { setSelected(selected.size === transactions.length ? new Set() : new Set(transactions.map(t => t.id))); };

  // ---- Edit Row ----
  const startEdit = (tx) => {
    setEditingRow(tx.id);
    setEditData({ tag: tx.tag || 'untagged', sub_category: tx.sub_category || '', category: tx.category || 'Other' });
  };

  const cancelEdit = () => { setEditingRow(null); setEditData({ tag: '', sub_category: '', category: '' }); };

  const saveEdit = async (txId) => {
    try {
      const promises = [];
      const tx = transactions.find(t => t.id === txId);
      if (editData.tag !== tx.tag) {
        promises.push(axios.patch(`${API}/transactions/${txId}/tag`, { tag: editData.tag }, { withCredentials: true }));
      }
      if (editData.category !== tx.category) {
        promises.push(axios.patch(`${API}/transactions/${txId}/category`, { category: editData.category }, { withCredentials: true }));
      }
      if (editData.sub_category !== (tx.sub_category || '') && editData.sub_category && editData.sub_category !== '__none__') {
        promises.push(axios.patch(`${API}/transactions/${txId}/sub-category`, { sub_category: editData.sub_category }, { withCredentials: true }));
      }
      if (editData.sub_category === '__none__' && tx.sub_category) {
        promises.push(axios.patch(`${API}/transactions/${txId}/sub-category`, { sub_category: '' }, { withCredentials: true }));
      }
      await Promise.all(promises);
      toast.success('Saved');
      setEditingRow(null);
      fetchTransactions(); // Refresh to get propagated changes
    } catch { toast.error('Save failed'); }
  };

  const handleSubCatChange = (v) => {
    if (v === '__others__') {
      setOthersDialog({ open: true, parentTag: editData.tag, customName: '' });
      return;
    }
    setEditData(d => ({ ...d, sub_category: v }));
  };

  const handleCatChange = (v) => {
    if (v === '__add__') {
      setCatDialog({ open: true, customName: '' });
      return;
    }
    setEditData(d => ({ ...d, category: v }));
  };

  const saveCustomSubCategory = async () => {
    const name = othersDialog.customName.trim();
    if (!name) { toast.error('Enter a name'); return; }
    try {
      await axios.post(`${API}/sub-categories`, { name, parent_tag: othersDialog.parentTag }, { withCredentials: true });
      const { data } = await axios.get(`${API}/sub-categories`, { withCredentials: true });
      setSubCatOptions(data);
      setEditData(d => ({ ...d, sub_category: name }));
      setOthersDialog({ open: false, parentTag: '', customName: '' });
      toast.success(`"${name}" added`);
    } catch { toast.error('Failed'); }
  };

  const saveCustomCategory = async () => {
    const name = catDialog.customName.trim();
    if (!name) { toast.error('Enter a name'); return; }
    try {
      await axios.post(`${API}/categories`, { name }, { withCredentials: true });
      const { data } = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(data.categories || DEFAULT_CATEGORIES);
      setEditData(d => ({ ...d, category: name }));
      setCatDialog({ open: false, customName: '' });
      toast.success(`"${name}" added`);
    } catch { toast.error('Failed'); }
  };

  const bulkTag = async (tag) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      await axios.post(`${API}/transactions/bulk-tag`, { transaction_ids: ids, tag }, { withCredentials: true });
      setSelected(new Set());
      toast.success(`Tagged ${ids.length} as ${tag}`);
      fetchTransactions();
    } catch { toast.error('Bulk tag failed'); }
  };

  const linkSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length < 2) { toast.error('Select at least 2 transactions'); return; }
    try {
      await axios.post(`${API}/transactions/link`, { transaction_ids: ids, note: linkDialog.note }, { withCredentials: true });
      setSelected(new Set());
      setLinkDialog({ open: false, note: '' });
      toast.success('Transactions linked as split entry');
      fetchTransactions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); }
  };

  const unlinkGroup = async (linkId) => {
    try {
      await axios.delete(`${API}/transactions/link/${linkId}`, { withCredentials: true });
      toast.success('Transactions unlinked');
      setExpandedLinks(prev => { const n = new Set(prev); n.delete(linkId); return n; });
      fetchTransactions();
    } catch { toast.error('Unlink failed'); }
  };

  const toggleLinkExpand = (linkId) => {
    setExpandedLinks(prev => { const n = new Set(prev); n.has(linkId) ? n.delete(linkId) : n.add(linkId); return n; });
  };

  // Group transactions: linked ones collapse into a single row
  const groupedTransactions = (() => {
    const result = [];
    const seen = new Set();
    for (const tx of transactions) {
      if (tx.link_id && linksMap[tx.link_id]) {
        if (seen.has(tx.link_id)) continue;
        seen.add(tx.link_id);
        const linkInfo = linksMap[tx.link_id];
        const children = transactions.filter(t => t.link_id === tx.link_id);
        result.push({
          _isGroup: true,
          link_id: tx.link_id,
          note: linkInfo.note,
          total_amount: linkInfo.total_amount,
          earliest_date: linkInfo.earliest_date,
          children,
          tag: children[0]?.tag || 'income',
          category: children[0]?.category || 'Salary',
          source: children[0]?.source,
          card_last_four: children[0]?.card_last_four,
          is_credit: (() => {
            const creditSum = children.filter(c => c.is_credit).reduce((s, c) => s + c.amount, 0);
            return Math.abs(creditSum - linkInfo.total_amount) < 1;
          })(),
          fy_india: children[0]?.fy_india,
          region: children[0]?.region,
        });
      } else {
        result.push(tx);
      }
    }
    return result;
  })();

  const downloadExcel = async () => {
    setExporting(true);
    try {
      const params = { sort_by: sort.field, sort_order: sort.order };
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all' && k !== 'junk' && k !== 'search') params[k] = v; });
      if (filters.month !== 'all') params.month = filters.month;
      const response = await axios.get(`${API}/transactions/export`, { params, withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.headers['content-disposition']?.split('filename="')[1]?.replace('"', '') || 'transactions.xlsx');
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="transactions-page">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1C1C19]">Transactions</h2>
        <Button onClick={downloadExcel} disabled={exporting || total === 0} data-testid="download-excel-btn"
          className="bg-[#284A33] text-white hover:bg-[#1C3624] text-xs h-8 px-3">
          <Download size={13} className="mr-1.5" />{exporting ? 'Exporting...' : `Download Excel (${total})`}
        </Button>
      </div>

      {/* Pending / Tagged / Junk Tabs */}
      <div className="flex items-center border-b border-[#E2E0D8]" data-testid="transaction-tabs">
        <button onClick={() => switchTab('pending')} data-testid="tab-pending"
          className={`px-5 py-2.5 text-sm font-medium transition-colors relative ${txnTab === 'pending' ? 'text-[#284A33]' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          Pending
          {txnTab === 'pending' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#284A33] rounded-t" />}
        </button>
        <button onClick={() => switchTab('business')} data-testid="tab-business"
          className={`px-5 py-2.5 text-sm font-medium transition-colors relative ${txnTab === 'business' ? 'text-red-600' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          Business
          {txnTab === 'business' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-600 rounded-t" />}
        </button>
        <button onClick={() => switchTab('personal')} data-testid="tab-personal"
          className={`px-5 py-2.5 text-sm font-medium transition-colors relative ${txnTab === 'personal' ? 'text-orange-600' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          Personal
          {txnTab === 'personal' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600 rounded-t" />}
        </button>
        <button onClick={() => switchTab('dubai_recovery')} data-testid="tab-dubai-recovery"
          className={`px-5 py-2.5 text-sm font-medium transition-colors relative ${txnTab === 'dubai_recovery' ? 'text-amber-600' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
          Dubai Recovery
          {txnTab === 'dubai_recovery' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-600 rounded-t" />}
        </button>
        {/* Inflow / Outflow / Junk / Minor sub-tabs */}
        <div className="ml-auto flex items-center gap-1 pr-1" data-testid="flow-tabs">
          {[
            { key: 'all', label: 'All' },
            { key: 'inflow', label: 'Inflow' },
            { key: 'outflow', label: 'Outflow' },
            ...(txnTab === 'personal' ? [
              { key: 'junk', label: 'Junk' },
              { key: 'minor', label: 'Minor' },
            ] : txnTab === 'business' ? [
              { key: 'junk', label: 'Junk' },
            ] : []),
          ].map(({ key, label }) => (
            <button key={key} onClick={() => switchFlowTab(key)} data-testid={`flow-${key}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${flowTab === key ? (key === 'junk' ? 'bg-[#5C5C55] text-white' : key === 'minor' ? 'bg-[#7C6B4F] text-white' : 'bg-[#284A33] text-white') : 'text-[#5C5C55] hover:bg-[#E8E6DE]'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Source tabs — shown across all main tabs */}
      {(filterOptions.sources || []).length > 0 && (
        <div className="flex items-center gap-1 overflow-x-auto pb-1 -mt-1" data-testid="source-tabs">
          <button onClick={() => { setFilters(f => ({ ...f, source: 'all', card: 'all', country: 'all' })); setPage(1); setSelected(new Set()); }}
            data-testid="source-tab-all"
            className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${filters.source === 'all' ? 'bg-[#1C1C19] text-white' : 'text-[#5C5C55] hover:bg-[#E8E6DE] border border-[#E2E0D8]'}`}>
            All
          </button>
          {(() => {
            // Expand multi-card sources into separate tabs
            const expanded = [];
            for (const s of (filterOptions.sources || [])) {
              if (s.card_breakdown && s.card_breakdown.length > 1) {
                for (const cb of s.card_breakdown) {
                  expanded.push({ source: s.name, card: cb.card, count: cb.count, display: `${s.name} ****${cb.card}` });
                }
              } else {
                expanded.push({ source: s.name, card: 'all', count: s.count, display: s.name });
              }
            }
            expanded.sort((a, b) => a.count - b.count);
            return expanded.map(tab => (
              <button key={`${tab.source}-${tab.card}`}
                onClick={() => { setFilters(f => ({ ...f, source: tab.source, card: tab.card, country: 'all' })); setPage(1); setSelected(new Set()); }}
                data-testid={`source-tab-${tab.display.toLowerCase().replace(/[\s*]+/g, '-')}`}
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  filters.source === tab.source && filters.card === tab.card ? 'bg-[#1C1C19] text-white' : 'text-[#5C5C55] hover:bg-[#E8E6DE] border border-[#E2E0D8]'}`}>
                {tab.display} <span className="text-[10px] opacity-60 ml-0.5">{tab.count}</span>
              </button>
            ));
          })()}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="transaction-filters">
        <div className="relative flex-1 min-w-[160px] max-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C55]" />
          <Input data-testid="search-input" placeholder="Search transactions..." value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)} className="pl-8 border-[#E2E0D8] h-8 text-xs" />
        </div>
        {/* Holder sub-filter — show when a multi-holder source tab is selected */}
        {filters.source !== 'all' && (() => {
          const srcInfo = (filterOptions.sources || []).find(s => s.name === filters.source);
          const holders = srcInfo?.holders?.filter(h => h && h !== 'None' && h !== 'Unknown') || [];
          const cards = srcInfo?.cards?.filter(c => c && c !== 'None') || [];
          if (holders.length > 1) {
            return (
              <Select value={filters.card} onValueChange={(v) => { handleFilterChange('card', v); }}>
                <SelectTrigger data-testid="filter-holder" className="w-[150px] h-8 border-[#E2E0D8] text-xs">
                  <SelectValue placeholder="All Holders" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Holders</SelectItem>
                  {holders.map(h => (
                    <SelectItem key={h} value={h}>{h}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          } else if (cards.length > 1) {
            return (
              <Select value={filters.card} onValueChange={(v) => { handleFilterChange('card', v); }}>
                <SelectTrigger data-testid="filter-card" className="w-[110px] h-8 border-[#E2E0D8] text-xs">
                  <SelectValue placeholder="All Cards" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cards</SelectItem>
                  {cards.map(c => (
                    <SelectItem key={c} value={c}>****{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }
          return null;
        })()}
        {/* Country filter */}
        {(filterOptions.countries || []).length > 1 && (
          <Select value={filters.country} onValueChange={(v) => { handleFilterChange('country', v); }}>
            <SelectTrigger data-testid="filter-country" className="w-[120px] h-8 border-[#E2E0D8] text-xs">
              <SelectValue placeholder="All Countries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              {(filterOptions.countries || []).map(c => (
                <SelectItem key={c.name} value={c.name}>{c.name} ({c.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {[
          { key: 'month', testId: 'filter-month', icon: Calendar, w: 'w-[120px]', label: 'All Months', opts: (filterOptions.months || []).map(m => ({ v: m, l: fmtMonth(m) })) },
          ...(txnTab === 'tagged' ? [{ key: 'tag', testId: 'filter-tag', w: 'w-[110px]', label: 'All Tags', opts: [{ v: 'personal', l: 'Personal' }, { v: 'business', l: 'Business' }, { v: 'income', l: 'Income' }] }] : []),
          /* category filter moved to capsule row below */
          { key: 'fy', testId: 'filter-fy', w: 'w-[110px]', label: 'All FYs', opts: activeFyList.map(f => ({ v: f, l: f })) },
        ].map(({ key, testId, icon: Ic, w, label, opts }) => (
          <Select key={key} value={filters[key] === 'tagged' ? 'all' : filters[key]} onValueChange={(v) => {
            if (key === 'tag') {
              setFilters(f => ({ ...f, tag: v === 'all' ? 'tagged' : v }));
              setPage(1);
            } else {
              handleFilterChange(key, v);
            }
          }}>
            <SelectTrigger data-testid={testId} className={`${w} h-8 border-[#E2E0D8] text-xs`}>
              {Ic && <Ic size={12} className="mr-1 text-[#5C5C55]" />}<SelectValue placeholder={label} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{label}</SelectItem>
              {opts.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
        <span className="text-[11px] text-[#5C5C55] ml-auto font-mono">{total} results</span>
      </div>

      {/* Category Capsules */}
      {(filterOptions.categories || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="category-capsules">
          <button
            data-testid="category-capsule-all"
            onClick={() => handleFilterChange('category', 'all')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
              filters.category === 'all'
                ? 'bg-[#284A33] text-white border-[#284A33]'
                : 'bg-white text-[#5C5C55] border-[#E2E0D8] hover:border-[#284A33]/40 hover:text-[#284A33]'
            }`}
          >
            All
          </button>
          {filterOptions.categories.map(c => (
            <button
              key={c}
              data-testid={`category-capsule-${c.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              onClick={() => handleFilterChange('category', c)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                filters.category === c
                  ? 'bg-[#284A33] text-white border-[#284A33]'
                  : 'bg-white text-[#5C5C55] border-[#E2E0D8] hover:border-[#284A33]/40 hover:text-[#284A33]'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-[#284A33]/5 border border-[#284A33]/15 rounded-md px-4 py-2.5" data-testid="bulk-actions">
          <span className="text-sm text-[#284A33] font-medium">{selected.size} selected</span>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" onClick={() => bulkTag('personal')} className="bg-[#4A7A5D]/10 text-[#4A7A5D] hover:bg-[#4A7A5D]/20 border border-[#4A7A5D]/20 h-7 text-xs"><User size={12} className="mr-1" /> Personal</Button>
            <Button size="sm" onClick={() => bulkTag('business')} className="bg-[#D36135]/10 text-[#D36135] hover:bg-[#D36135]/20 border border-[#D36135]/20 h-7 text-xs"><Briefcase size={12} className="mr-1" /> Business</Button>
            {selected.size >= 2 && (
              <Button size="sm" onClick={() => setLinkDialog({ open: true, note: '' })} data-testid="link-split-btn"
                className="bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 h-7 text-xs"><Link2 size={12} className="mr-1" /> Link as Split</Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => bulkTag('untagged')} className="text-[#5C5C55] hover:bg-[#E8E6DE] h-7 text-xs">Clear</Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-[#E2E0D8] rounded-md overflow-hidden">
        {loading ? <div className="p-8 text-center text-sm text-[#5C5C55]">Loading...</div> :
         transactions.length === 0 ? <div className="p-8 text-center text-sm text-[#5C5C55]">No transactions</div> : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="transactions-table">
              <thead>
                <tr className="border-b border-[#E2E0D8] bg-[#F7F6F2]/50">
                  <th className="text-left p-2 text-[#5C5C55]"><SortHeader label="Date" field="date" currentSort={sort} onSort={handleSort} /></th>
                  <th className="text-left p-2 text-[#5C5C55]"><SortHeader label="Description" field="merchant" currentSort={sort} onSort={handleSort} /></th>
                  <th className="text-left p-2 text-[10px] tracking-[0.15em] uppercase text-[#5C5C55] font-medium">Entity</th>
                  <th className="text-left p-2 text-[10px] tracking-[0.15em] uppercase text-[#5C5C55] font-medium">Tag</th>
                  <th className="text-right p-2 text-[#5C5C55]"><SortHeader label="Amount" field="amount" currentSort={sort} onSort={handleSort} /></th>
                  <th className="w-10 p-2 text-[10px] tracking-[0.15em] uppercase text-[#5C5C55] font-medium text-center">Edit</th>
                </tr>
              </thead>
              <tbody>
                {groupedTransactions.map((item) => {
                  if (item._isGroup) {
                    const isExpanded = expandedLinks.has(item.link_id);
                    return (
                      <React.Fragment key={`link-${item.link_id}`}>
                        {/* Collapsed group summary row */}
                        <tr data-testid={`linked-group-${item.link_id}`}
                          className="border-b border-blue-200/60 bg-blue-50/30 hover:bg-blue-50/60 transition-colors cursor-pointer"
                          onClick={() => toggleLinkExpand(item.link_id)}>
                          <td className="p-2">
                            <div className="text-sm font-mono whitespace-nowrap text-[#1C1C19]">{item.earliest_date}</div>
                            <div className="text-[10px] font-mono text-[#5C5C55]/60">{item.fy_india}</div>
                          </td>
                          <td className="p-2">
                            <div title={item.children.map(c => c.merchant).join('\n')} className="text-sm font-medium text-[#1C1C19] cursor-help">
                              {(() => {
                                const creditChild = item.children.find(c => c.is_credit);
                                const displayChild = creditChild || item.children[0];
                                return (!displayChild?.tag || displayChild?.tag === 'untagged') ? (displayChild?.merchant || '—') : (displayChild?.sub_category || displayChild?.category || displayChild?.merchant || '—');
                              })()}
                            </div>
                            <div className="mt-0.5 space-y-px">
                              {item.children.map((c, i) => {
                                // Derive a short label from merchant for linked children
                                let label = c.sub_category;
                                if (!label || label === (item.children.find(x => x.is_credit)?.sub_category || item.children[0]?.sub_category || item.children[0]?.category)) {
                                  const m = (c.merchant || '').toUpperCase();
                                  if (m.includes('CGST')) label = 'CGST';
                                  else if (m.includes('SGST')) label = 'SGST';
                                  else if (m.includes('IGST')) label = 'IGST';
                                  else if (m.includes('TAX ON INT') || m.includes('TDS')) label = 'TDS';
                                  else if (m.includes('INTEREST')) label = 'Interest';
                                  else if (m.includes('NACH') || m.includes('EMI')) label = 'EMI';
                                  else if (m.includes('MAINT CHG') || m.includes('BANK CHARGE')) label = 'Bank Charges';
                                  else label = c.sub_category || c.merchant?.split(' ').slice(0, 3).join(' ') || 'Other';
                                }
                                return (
                                  <div key={i} className="text-[10px] text-[#5C5C55] font-mono truncate max-w-[300px]" title={c.merchant}>
                                    {label}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                          <td className="p-2">
                            <div className="flex items-center gap-1">
                              <EntityIcon source={item.source} />
                              <span className="text-[11px] text-[#5C5C55] font-mono">{item.source || '?'}</span>
                            </div>
                            {item.children[0]?.card_last_four && item.children[0].card_last_four !== 'None' && <div className="text-[10px] font-mono text-[#5C5C55]/60">****{item.children[0].card_last_four}</div>}
                          </td>
                          <td className="p-2">
                            <TagBadge tag={item.tag} tagSource={item.children?.[0]?.tag_source} isCredit={item.is_credit} category={item.category || item.children?.[0]?.category} />
                          </td>
                          <td className="p-2 text-right">
                            <span className={`font-mono text-sm font-semibold ${amtColor({is_credit: item.is_credit, tag: item.tag})}`}>
                              {item.is_credit ? '+' : ''}₹{fmtINR(item.total_amount)}
                            </span>
                            <div className="text-[10px] font-mono text-[#5C5C55]/60">&nbsp;</div>
                            <div className="mt-0.5 space-y-px">
                              {item.children.map((c, i) => (
                                <div key={i} className={`text-[10px] font-mono ${amtColor(c)}`}>
                                  {c.is_credit ? '+' : ''}₹{fmtINR(c.amount)}
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="p-2">
                            <button onClick={(e) => { e.stopPropagation(); unlinkGroup(item.link_id); }} title="Unlink"
                              data-testid={`unlink-${item.link_id}`}
                              className="p-1 rounded text-[#5C5C55] hover:text-red-600 hover:bg-red-50 transition-all"><Unlink size={12} /></button>
                          </td>
                        </tr>
                        {/* Expanded child rows */}
                        {isExpanded && item.children.map((tx) => {
                          const isEditing = editingRow === tx.id;
                          const subCats = isEditing ? (subCatOptions[editData.tag] || []) : [];
                          return (
                            <tr key={tx.id} data-testid={`transaction-row-${tx.id}`}
                              className="border-b border-blue-100/60 bg-blue-50/15 transition-colors">
                              <td className="p-2">
                                <div className="text-sm font-mono whitespace-nowrap text-[#1C1C19]/70">{tx.date}</div>
                              </td>
                              <td className="p-2">
                                <div title={tx.merchant || ''} className="text-sm text-[#1C1C19]/70 truncate max-w-[450px] cursor-help">{(!tx.tag || tx.tag === 'untagged') ? (tx.merchant || '—') : (tx.sub_category || tx.category || tx.merchant || '—')}</div>
                              </td>
                              <td className="p-2">
                                <span className="text-[11px] text-[#5C5C55]/60 font-mono">{tx.source}</span>
                              </td>
                              <td className="p-2">
                                {isEditing ? (
                                  <div className="flex flex-col gap-1.5">
                                    <Select value={editData.tag} onValueChange={(v) => setEditData(d => ({ ...d, tag: v, sub_category: '' }))}>
                                      <SelectTrigger className="h-7 w-[120px] text-xs border-[#284A33]/30"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="untagged">Untagged</SelectItem>
                                        {tx.is_credit ? <SelectItem value="income">Income</SelectItem> : <><SelectItem value="personal">Personal</SelectItem><SelectItem value="business">Business</SelectItem></>}
                                      </SelectContent>
                                    </Select>
                                    <Select value={editData.category} onValueChange={handleCatChange}>
                                      <SelectTrigger className="h-7 w-[120px] text-xs border-[#E2E0D8]"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        {(tx.is_credit ? INCOME_CATEGORIES : categories).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        <SelectItem value="__add__" className="text-[#284A33] font-medium">+ Add Category</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                ) : (
                                  <div className="flex flex-col gap-0.5">
                                    <TagBadge tag={tx.tag} tagSource={tx.tag_source} isCredit={tx.is_credit} category={tx.category} />
                                  </div>
                                )}
                              </td>
                              <td className="p-2 text-right">
                                <span className={`font-mono text-sm ${amtColor(tx)} opacity-70`}>
                                  {tx.is_credit ? '+' : ''}₹{fmtINR(tx.amount)}
                                </span>
                              </td>
                              <td className="p-2">
                                <div className="flex items-center gap-0.5 justify-center">
                                  {isEditing ? (
                                    <>
                                      <button onClick={() => saveEdit(tx.id)} title="Save" className="p-1 rounded text-[#284A33] hover:bg-[#284A33]/10"><Save size={13} /></button>
                                      <button onClick={cancelEdit} title="Cancel" className="p-1 rounded text-[#5C5C55] hover:bg-[#E8E6DE]"><X size={13} /></button>
                                    </>
                                  ) : (
                                    <button onClick={() => startEdit(tx)} title="Edit" className="p-1 rounded text-[#5C5C55] hover:text-[#284A33] hover:bg-[#284A33]/10"><Pencil size={12} /></button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  }

                  // Regular (non-linked) transaction row
                  const tx = item;
                  const isEditing = editingRow === tx.id;
                  const subCats = isEditing ? (subCatOptions[editData.tag] || []) : [];

                  return (
                    <tr key={tx.id} data-testid={`transaction-row-${tx.id}`}
                      className={`border-b border-[#E2E0D8]/60 transition-colors ${isEditing ? 'bg-[#284A33]/[0.02]' : 'hover:bg-[#F7F6F2]/50'}`}>
                      {/* Date */}
                      <td className="p-2">
                        <div className="text-sm font-mono whitespace-nowrap text-[#1C1C19]">{tx.date}</div>
                        <div className="text-[10px] font-mono text-[#5C5C55]/60">{tx.fy_india}</div>
                      </td>
                      {/* Description from statement */}
                      <td className="p-2">
                        <div className="flex items-center gap-1.5">
                          <div title={tx.merchant || ''} className="text-sm font-medium truncate max-w-[450px] cursor-help text-[#1C1C19]">{(!tx.tag || tx.tag === 'untagged') ? (tx.merchant || '—') : (tx.sub_category || tx.category || tx.merchant || '—')}</div>
                          {tx.is_junk && txnTab !== 'junk' && <span className="text-[9px] bg-red-100 text-red-600 px-1 py-0.5 rounded font-mono shrink-0">Junk</span>}
                          {txnTab !== 'dubai_recovery' && tx.region === 'uae' && tx.tag === 'business' && (
                            <span className="text-[9px] bg-purple-100 text-purple-700 px-1 py-0.5 rounded font-mono shrink-0 flex items-center gap-0.5"><Building2 size={8} />Claimable</span>
                          )}
                          {txnTab !== 'dubai_recovery' && tx.region === 'uae' && tx.tag !== 'business' && (
                            <span className="text-[9px] bg-[#D36135]/10 text-[#D36135] px-1 py-0.5 rounded font-mono shrink-0">UAE</span>
                          )}
                        </div>
                        {tx.location && <div title={tx.location} className="text-[10px] text-[#5C5C55] truncate max-w-[350px] cursor-help">{tx.location}</div>}
                      </td>
                      {/* Entity */}
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          <EntityIcon source={tx.source} />
                          <span className="text-[11px] text-[#5C5C55] font-mono">{tx.source || '?'}</span>
                        </div>
                        {tx.card_last_four && tx.card_last_four !== 'None' && <div className="text-[10px] font-mono text-[#5C5C55]/60">****{tx.card_last_four}</div>}
                      </td>
                      {/* Tag / Category — View or Edit mode */}
                      <td className="p-2">
                        {isEditing ? (
                          <div className="flex flex-col gap-1.5">
                            <Select value={editData.tag} onValueChange={(v) => setEditData(d => ({ ...d, tag: v, sub_category: '' }))}>
                              <SelectTrigger className="h-7 w-[120px] text-xs border-[#284A33]/30"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="untagged">Untagged</SelectItem>
                                {tx.is_credit ? (
                                  <SelectItem value="income">Income</SelectItem>
                                ) : (
                                  <>
                                    <SelectItem value="personal">Personal</SelectItem>
                                    <SelectItem value="business">Business</SelectItem>
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                            {(editData.tag === 'personal' || editData.tag === 'business') && (
                              <Select value={editData.sub_category || '__none__'} onValueChange={handleSubCatChange}>
                                <SelectTrigger className="h-6 w-[130px] text-[10px] border-[#E2E0D8]"><SelectValue placeholder="Sub-category" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__" className="text-[#999]">No sub-category</SelectItem>
                                  {subCats.map(sc => <SelectItem key={sc} value={sc}>{sc}</SelectItem>)}
                                  <SelectItem value="__others__" className="text-[#284A33] font-medium">+ Others (custom)</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            <Select value={editData.category} onValueChange={handleCatChange}>
                              <SelectTrigger className="h-7 w-[120px] text-xs border-[#E2E0D8]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {(tx.is_credit ? INCOME_CATEGORIES : categories).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                <SelectItem value="__add__" className="text-[#284A33] font-medium">+ Add Category</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            <TagBadge tag={tx.tag} tagSource={tx.tag_source} isCredit={tx.is_credit} category={tx.category} />
                          </div>
                        )}
                      </td>
                      {/* Amount + Country */}
                      <td className="p-2 text-right">
                        <span className={`font-mono text-sm ${amtColor(tx)}`}>
                          {tx.is_credit ? '+' : ''}₹{fmtINR(tx.amount)}
                        </span>
                        {tx.foreign_amount && (
                          <div className="text-[10px] font-mono text-[#5C5C55] flex items-center justify-end gap-1">
                            <span className="bg-[#E8E6DE] px-1 py-0 rounded text-[9px]">{tx.country || tx.foreign_currency}</span>
                            {tx.foreign_currency} {tx.foreign_amount}
                          </div>
                        )}
                      </td>
                      {/* Actions: Edit/Save/Cancel */}
                      <td className="p-2">
                        <div className="flex items-center gap-0.5 justify-center">
                          {isEditing ? (
                            <>
                              <button onClick={() => saveEdit(tx.id)} data-testid={`save-${tx.id}`} title="Save"
                                className="p-1 rounded text-[#284A33] hover:bg-[#284A33]/10 transition-all"><Save size={13} /></button>
                              <button onClick={cancelEdit} data-testid={`cancel-${tx.id}`} title="Cancel"
                                className="p-1 rounded text-[#5C5C55] hover:bg-[#E8E6DE] transition-all"><X size={13} /></button>
                            </>
                          ) : (
                            <button onClick={() => startEdit(tx)} data-testid={`edit-${tx.id}`} title="Edit tag & category"
                              className="p-1 rounded text-[#5C5C55] hover:text-[#284A33] hover:bg-[#284A33]/10 transition-all"><Pencil size={12} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E0D8] bg-[#F7F6F2]/30">
            <span className="text-xs text-[#5C5C55]">Page {page} of {pages}</span>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-7 w-7 p-0"><ChevronLeft size={15} /></Button>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="h-7 w-7 p-0"><ChevronRight size={15} /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Custom Sub-category Dialog */}
      <Dialog open={othersDialog.open} onOpenChange={(o) => !o && setOthersDialog({ ...othersDialog, open: false })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Custom Sub-category</DialogTitle>
            <DialogDescription>Added to your {othersDialog.parentTag} sub-categories permanently.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Name</Label>
            <Input data-testid="custom-subcat-input" value={othersDialog.customName}
              onChange={(e) => setOthersDialog({ ...othersDialog, customName: e.target.value })}
              placeholder="e.g. Travel - Singapore" className="border-[#E2E0D8]"
              onKeyDown={(e) => e.key === 'Enter' && saveCustomSubCategory()} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOthersDialog({ ...othersDialog, open: false })}>Cancel</Button>
            <Button onClick={saveCustomSubCategory} className="bg-[#284A33] text-white hover:bg-[#1C3624]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Category Dialog */}
      <Dialog open={catDialog.open} onOpenChange={(o) => !o && setCatDialog({ ...catDialog, open: false })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Custom Category</DialogTitle>
            <DialogDescription>Added to your categories permanently.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Category Name</Label>
            <Input data-testid="custom-category-input" value={catDialog.customName}
              onChange={(e) => setCatDialog({ ...catDialog, customName: e.target.value })}
              placeholder="e.g. Gym & Fitness" className="border-[#E2E0D8]"
              onKeyDown={(e) => e.key === 'Enter' && saveCustomCategory()} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCatDialog({ ...catDialog, open: false })}>Cancel</Button>
            <Button onClick={saveCustomCategory} className="bg-[#284A33] text-white hover:bg-[#1C3624]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link as Split Dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(o) => !o && setLinkDialog({ ...linkDialog, open: false })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Link2 size={16} className="text-blue-600" /> Link as Split Entry</DialogTitle>
            <DialogDescription>Combine {selected.size} transactions into one split entry. They'll appear as a single row with the combined total.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Note (optional)</Label>
            <Input data-testid="link-note-input" value={linkDialog.note}
              onChange={(e) => setLinkDialog({ ...linkDialog, note: e.target.value })}
              placeholder="e.g. TDS wrongly deducted, then refunded" className="border-[#E2E0D8]"
              onKeyDown={(e) => e.key === 'Enter' && linkSelected()} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLinkDialog({ ...linkDialog, open: false })}>Cancel</Button>
            <Button onClick={linkSelected} data-testid="confirm-link-btn" className="bg-blue-600 text-white hover:bg-blue-700">
              <Link2 size={13} className="mr-1.5" /> Link Transactions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
